import { useEffect, useRef, useState } from 'react';

export interface AircraftIdentity {
  registrationMark: string;
  aircraftKind: '' | 'airplane' | 'helicopter';
  manufacturer: string;
  model: string;
  serialNumber: string;
  legalOwner: string;
  responsibleOperator: string;
}
export const normalizeRegistration = (value: string) =>
  value.trim().toUpperCase().replace(/[-\s]/g, '');

export function AircraftRegistrationLookup({
  value,
  onChange,
  onVerified,
  onExpiredSession,
}: {
  value: AircraftIdentity;
  onChange: (value: AircraftIdentity) => void;
  onVerified: (mark: string) => void;
  onExpiredSession: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  async function lookup() {
    if (!/^[A-Z]{2}[-\s]?[A-Z0-9]{3}$/i.test(value.registrationMark.trim())) {
      setMessage('Informe a matrícula: PR-ABC, PR ABC ou PRABC.');
      return;
    }
    const mark = normalizeRegistration(value.registrationMark);
    setBusy(true);
    setMessage('');
    onVerified('');
    try {
      const response = await fetch(`/v1/reference/aircraft/${mark}`, {
        credentials: 'include',
        signal: AbortSignal.timeout(20000),
      });
      if (response.status === 401) {
        await onExpiredSession();
        return;
      }
      if (!response.ok) throw Error('unavailable');
      const body = (await response.json()) as { result: Partial<AircraftIdentity> | null };
      if (!active.current) return;
      if (!body.result) {
        setMessage(
          'Matrícula não encontrada no RAB. Confira a matrícula e tente novamente. O cadastro não foi liberado.',
        );
        return;
      }
      if (!body.result.aircraftKind || !body.result.manufacturer || !body.result.model) {
        setMessage(
          'O RAB não retornou os dados necessários de avião ou helicóptero. O cadastro não foi liberado.',
        );
        return;
      }
      onChange({
        registrationMark: mark,
        aircraftKind: body.result.aircraftKind,
        manufacturer: body.result.manufacturer,
        model: body.result.model,
        serialNumber: body.result.serialNumber || '',
        legalOwner: body.result.legalOwner || '',
        responsibleOperator: body.result.responsibleOperator || '',
      });
      onVerified(mark);
      setMessage(
        'Aeronave encontrada no RAB. Dados preenchidos automaticamente. Selecione as bases abaixo.',
      );
    } catch {
      setMessage('Não foi possível consultar o RAB. Tente novamente para liberar o cadastro.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="referenceLookup">
      <label>
        Matrícula <span className="requiredMark">*</span>
        <input
          required
          disabled={busy}
          value={value.registrationMark}
          maxLength={6}
          placeholder="PR-ABC, PR ABC ou PRABC"
          onChange={(e) => {
            onVerified('');
            setMessage('');
            onChange({
              registrationMark: e.target.value.toUpperCase(),
              aircraftKind: '',
              manufacturer: '',
              model: '',
              serialNumber: '',
              legalOwner: '',
              responsibleOperator: '',
            });
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void lookup();
            }
          }}
        />
      </label>
      <button type="button" disabled={busy} onClick={() => void lookup()}>
        {busy ? 'Consultando RAB…' : 'Buscar matrícula na ANAC'}
      </button>
      {message ? <p role="status">{message}</p> : null}
      {value.manufacturer ? (
        <div className="profileForm">
          <label>
            Tipo de aeronave
            <input
              readOnly
              value={
                value.aircraftKind === 'helicopter'
                  ? 'Helicóptero'
                  : value.aircraftKind === 'airplane'
                    ? 'Avião'
                    : ''
              }
            />
          </label>
          <label>
            Fabricante
            <input readOnly value={value.manufacturer} />
          </label>
          <label>
            Modelo
            <input readOnly value={value.model} />
          </label>
          <label>
            Número de série
            <input readOnly value={value.serialNumber} />
          </label>
          <label>
            Proprietário
            <input readOnly value={value.legalOwner} />
          </label>
          <label>
            Operador registrado
            <input readOnly value={value.responsibleOperator} />
          </label>
        </div>
      ) : null}
    </div>
  );
}
